// -----------------------------------------------------------
// FloraIQ — Validação e Qualidade dos Dados (Página Principal)
// -----------------------------------------------------------

import { useState, useCallback } from "react";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { qualityKpiStats } from "@/mocks/quality";
import QualityOverview from "./components/QualityOverview";
import ValidationRulesTable from "./components/ValidationRulesTable";
import InconsistenciesTable from "./components/InconsistenciesTable";
import GisValidationPanel from "./components/GisValidationPanel";
import TreeValidationPanel from "./components/TreeValidationPanel";
import SourceEvidenceValidationPanel from "./components/SourceEvidenceValidationPanel";
import RrrAuthorizationValidationPanel from "./components/RrrAuthorizationValidationPanel";
import PointCloudValidationPanel from "./components/PointCloudValidationPanel";
import AssistedCorrectionsPanel from "./components/AssistedCorrectionsPanel";
import QualityReportsGrid from "./components/QualityReportsGrid";

type TabId = "overview" | "rules" | "inconsistencies" | "gis" | "arboreo" | "sources" | "rrr" | "nuvem" | "correcoes" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "rules", label: "Regras de Validação", icon: "ri-list-check-3" },
  { id: "inconsistencies", label: "Inconsistências", icon: "ri-error-warning-line" },
  { id: "gis", label: "Validação GIS", icon: "ri-map-pin-line" },
  { id: "arboreo", label: "Validação Arbórea", icon: "ri-plant-line" },
  { id: "sources", label: "Fontes e Evidências", icon: "ri-folder-info-line" },
  { id: "rrr", label: "RRR e Autorizações", icon: "ri-shield-check-line" },
  { id: "nuvem", label: "Nuvem de Pontos", icon: "ri-stack-line" },
  { id: "correcoes", label: "Correções Assistidas", icon: "ri-tools-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

export default function DataQualityPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [validating, setValidating] = useState(false);

  const handleValidate = useCallback(() => {
    setValidating(true);
    setActiveTab("overview");
    setTimeout(() => setValidating(false), 1500);
  }, []);

  const handleReprocessGis = useCallback(() => {
    setActiveTab("gis");
  }, []);

  const handleExportInconsistencias = useCallback(() => {
    alert("Exportação de inconsistências iniciada! (Simulação)\n\nArquivo: inconsistencias_2026-07-01.xlsx\nRegistros: 126 críticas + 438 moderadas");
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <QualityOverview />;
      case "rules": return <ValidationRulesTable />;
      case "inconsistencies": return <InconsistenciesTable />;
      case "gis": return <GisValidationPanel />;
      case "arboreo": return <TreeValidationPanel />;
      case "sources": return <SourceEvidenceValidationPanel />;
      case "rrr": return <RrrAuthorizationValidationPanel />;
      case "nuvem": return <PointCloudValidationPanel />;
      case "correcoes": return <AssistedCorrectionsPanel />;
      case "reports": return <QualityReportsGrid />;
      default: return <QualityOverview />;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Validação e Qualidade dos Dados"
        subtitle="Monitore a consistência técnica, territorial, documental, GIS e metodológica dos dados do CTA e da metodologia FloraIQ-LADM."
      >
        <button
          onClick={handleValidate}
          className={`text-xs px-3 py-2 cursor-pointer whitespace-nowrap ${validating ? "btn-primary opacity-70" : "btn-primary"}`}
          disabled={validating}
        >
          <i className={`${validating ? "ri-loader-4-line animate-spin" : "ri-check-double-line"} mr-1`}></i>
          <span className="hidden sm:inline">{validating ? "Validando..." : "Executar Validação Geral"}</span>
        </button>
        <button
          onClick={() => setActiveTab("inconsistencies")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-search-line mr-1"></i>
          <span className="hidden sm:inline">Validar Projeto</span>
        </button>
        <button
          onClick={handleReprocessGis}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-global-line mr-1"></i>
          <span className="hidden sm:inline">Reprocessar Regras GIS</span>
        </button>
        <button
          onClick={() => setActiveTab("reports")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-chart-line mr-1"></i>
          <span className="hidden sm:inline">Gerar Relatório de Qualidade</span>
        </button>
        <button
          onClick={handleExportInconsistencias}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-line mr-1"></i>
          <span className="hidden sm:inline">Exportar Inconsistências</span>
        </button>
        <button
          onClick={() => setActiveTab("rules")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-settings-3-line mr-1"></i>
          <span className="hidden sm:inline">Configurar Regras</span>
        </button>
      </PageHeader>

      {/* Validação em andamento */}
      {validating && (
        <div className="bg-blue-50/60 border border-blue-200/60 rounded-card p-3 flex items-center gap-3 slide-up">
          <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
          <div>
            <p className="text-sm font-medium text-blue-800">Validação em andamento...</p>
            <p className="text-xs text-blue-600">Executando 32 regras de validação sobre 12.486 registros</p>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard
          label="Qualidade Geral dos Dados"
          value={`${qualityKpiStats.qualidadeGeral}%`}
          icon="ri-check-double-line"
          variant={qualityKpiStats.qualidadeGeral >= 85 ? "success" : "warning"}
        />
        <KpiCard
          label="Árvores Avaliadas"
          value={qualityKpiStats.arvoresAvaliadas.toLocaleString("pt-BR")}
          icon="ri-plant-line"
          variant="default"
        />
        <KpiCard
          label="Registros Conformes"
          value={qualityKpiStats.registrosConformes.toLocaleString("pt-BR")}
          icon="ri-check-line"
          variant="success"
          trend={{ value: "86,8%", positive: true }}
        />
        <KpiCard
          label="Inconsistências Críticas"
          value={qualityKpiStats.inconsistenciasCriticas}
          icon="ri-close-circle-line"
          variant="danger"
        />
        <KpiCard
          label="Pendências Moderadas"
          value={qualityKpiStats.pendenciasModeradas}
          icon="ri-error-warning-line"
          variant="warning"
        />
        <KpiCard
          label="Duplicidades Suspeitas"
          value={qualityKpiStats.duplicidadesSuspeitas}
          icon="ri-stack-line"
          variant="warning"
        />
        <KpiCard
          label="Regras Ativas"
          value={qualityKpiStats.regrasAtivas}
          icon="ri-list-check-3"
          variant="info"
        />
        <KpiCard
          label="Última Validação"
          value={qualityKpiStats.ultimaValidacao}
          icon="ri-time-line"
          variant="info"
        />
      </div>

      {/* Aviso conceitual */}
      <div className="bg-blue-50/60 border border-blue-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-blue-600"></i>
          </div>
          <p>
            As validações verificam se cada árvore possui geometria, unidade espacial, fonte, histórico, documentos, fotos e vínculos administrativos adequados conforme a metodologia FloraIQ-LADM.
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-500 bg-blue-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
}